import {
  ConsumerApplicationIntegration,
  errorToString,
  IntegrationLabels,
} from '@oneethos/shared'
import { useState } from 'react'
import { useAppState } from '../hooks'
import api from '../api-client'
import { toast } from 'react-toastify'
import { Spinner } from '@fluentui/react'

export type SyncLookback = '6' | '24' | '48' | '168' | '720' | 'custom' | 'forever'

type SyncSinceResult = { since: number | string } | { error: string }

const LOOKBACK_OPTIONS: { value: SyncLookback; label: string; summary: string }[] = [
  {
    value: '6',
    label: 'Last 6 hours',
    summary: 'records modified in the last 6 hours',
  },
  {
    value: '24',
    label: 'Last 24 hours',
    summary: 'records modified in the last 24 hours',
  },
  {
    value: '48',
    label: 'Last 48 hours',
    summary: 'records modified in the last 48 hours',
  },
  {
    value: '168',
    label: 'Last 7 days',
    summary: 'records modified in the last 7 days',
  },
  {
    value: '720',
    label: 'Last 30 days',
    summary: 'records modified in the last 30 days',
  },
  {
    value: 'custom',
    label: 'Since a specific date',
    summary: 'records modified after the chosen date',
  },
  {
    value: 'forever',
    label: 'All records (full history)',
    summary: 'every record',
  },
]

export function resolveSyncSince(lookback: SyncLookback, customSince: string): SyncSinceResult {
  if (lookback === 'forever') {
    return { since: 'forever' }
  }

  if (lookback === 'custom') {
    const parsed = new Date(customSince)
    if (!customSince || Number.isNaN(parsed.getTime())) {
      return { error: 'Choose a valid start date and time.' }
    }
    if (parsed.getTime() > Date.now()) {
      return { error: 'Start date must be in the past.' }
    }
    return { since: parsed.toISOString() }
  }

  const hours = Number(lookback)
  if (!Number.isFinite(hours) || hours <= 0) {
    return { error: 'Choose a lookback window.' }
  }

  return { since: hours }
}

function syncScope(lookback: SyncLookback, customSince: string, label: string): string {
  if (lookback === 'custom') {
    const parsed = new Date(customSince)
    if (!Number.isNaN(parsed.getTime())) {
      return `records modified after ${parsed.toLocaleString()} from ${label}`
    }
  }

  const option = LOOKBACK_OPTIONS.find(item => item.value === lookback)
  return `${option?.summary ?? 'the selected records'} from ${label}`
}

type SyncImplicationsProps = {
  label: string
  isAbrigo: boolean
  isDecisionPro: boolean
}

const SyncImplications = ({ label, isAbrigo, isDecisionPro }: SyncImplicationsProps) => {
  return (
    <div className="alert alert-warning" role="alert">
      <strong>This writes data for the current tenant.</strong>
      <ul className="mb-0 mt-2">
        {isDecisionPro ? (
          <>
            <li>
              Pulls loan applications and installer records in the selected
              window from {label}, then overwrites matching fields here.
            </li>
            <li>
              Status, installer-note, and banker-comment changes can email
              installers and borrowers while the sync runs.
            </li>
            <li>
              A wide window or full history can run for a long time and load a
              large amount of data. A second sync started during that run can
              write the same records at the same time.
            </li>
          </>
        ) : null}
        {isAbrigo ? (
          <>
            <li>
              Abrigo sync loads every loan application for this tenant, updates
              installer status from workflows, and can email people about those
              status changes.
            </li>
            <li>
              Tenants with around 7,000 loans or more can hit database rate
              limits during this sync.
            </li>
          </>
        ) : null}
        {!isDecisionPro && !isAbrigo ? (
          <li>
            This tenant has no consumer application integration configured, so
            the sync request will be rejected.
          </li>
        ) : null}
        <li>
          The request returns as soon as the sync starts. Progress continues
          in the background.
        </li>
      </ul>
    </div>
  )
}

type DecisionProSyncOptionsProps = {
  lookback: SyncLookback
  customSince: string
  customError?: string
  label: string
  onLookbackChange: (value: SyncLookback) => void
  onCustomSinceChange: (value: string) => void
}

const DecisionProSyncOptions = ({
  lookback,
  customSince,
  customError,
  label,
  onLookbackChange,
  onCustomSinceChange,
}: DecisionProSyncOptionsProps) => {
  return (
    <>
      <div className="form-group">
        <label htmlFor="sync-lookback">Records modified since</label>
        <select
          id="sync-lookback"
          className="form-select"
          value={lookback}
          onChange={event => onLookbackChange(event.target.value as SyncLookback)}
        >
          {LOOKBACK_OPTIONS.map(option => (
            <option key={option.value} value={option.value}>{option.label}</option>
          ))}
        </select>
        <div className="form-text">
          The selected default is the last 48 hours. An API call that omits
          the window uses the last 6 hours, which is the unattended sync window.
        </div>
      </div>
      {lookback === 'custom' ? (
        <div className="form-group">
          <label htmlFor="sync-custom-since">Start date and time</label>
          <input
            id="sync-custom-since"
            type="datetime-local"
            className="form-control"
            value={customSince}
            onChange={event => onCustomSinceChange(event.target.value)}
          />
          {customError ? <div className="form-text text-danger">{customError}</div> : null}
        </div>
      ) : null}
      {lookback === 'forever' ? (
        <div className="alert alert-danger" role="alert">
          Full history reads every loan application and installer record from {label}.
          Expect a much longer run than a recent window, and emails for each
          status, note, or banker-comment change it finds.
        </div>
      ) : null}
    </>
  )
}

export const DataSync = () => {
  const [loading, setLoading] = useState(false)
  const [lookback, setLookback] = useState<SyncLookback>('48')
  const [customSince, setCustomSince] = useState('')
  const { tenant } = useAppState()
  const integration = tenant.config.consumerApplicationIntegration
  const label = IntegrationLabels[integration] || 'the configured system'
  const isAbrigo = integration === ConsumerApplicationIntegration.Abrigo
  const isDecisionPro = integration === ConsumerApplicationIntegration.DecisionPro
  const sinceResult = isDecisionPro ? resolveSyncSince(lookback, customSince) : undefined
  const sinceError = sinceResult && 'error' in sinceResult ? sinceResult.error : undefined
  const canSync = isAbrigo || (isDecisionPro && !sinceError)

  const startSync = () => {
    if (!canSync || loading) {
      return
    }

    const body = isDecisionPro && sinceResult && !('error' in sinceResult)
      ? { since: sinceResult.since }
      : {}
    const scope = isAbrigo
      ? `all Abrigo workflows for this tenant`
      : syncScope(lookback, customSince, label)
    const warning = isDecisionPro && lookback === 'forever'
      ? 'This reads the full history and can send a large number of status emails.'
      : 'Changed statuses, notes, and banker comments can send emails.'
    const confirmed = window.confirm(
      `Start a sync of ${scope}?\n\n${warning}\n\nThe sync keeps running after this page responds.`
    )
    if (!confirmed) {
      return
    }

    setLoading(true)
    api.post('/sync', body)
      .then(() => toast.success('Sync started. It continues in the background and may take several minutes.'))
      .catch(ex => toast.error(errorToString(ex)))
      .finally(() => setTimeout(() => setLoading(false), 3_000))
  }

  return (
    <div className="data-sync">
      <h5>Sync from {label}</h5>
      <SyncImplications
        label={label}
        isAbrigo={isAbrigo}
        isDecisionPro={isDecisionPro}
      />
      {isDecisionPro ? (
        <DecisionProSyncOptions
          lookback={lookback}
          customSince={customSince}
          customError={lookback === 'custom' ? sinceError : undefined}
          label={label}
          onLookbackChange={setLookback}
          onCustomSinceChange={setCustomSince}
        />
      ) : null}
      <div className="buttons">
        <button
          type="button"
          className="btn btn-danger"
          disabled={loading || !canSync}
          onClick={startSync}
        >
          {loading ? <Spinner /> : `Sync from ${label}`}
        </button>
      </div>
    </div>
  )
}
